import { useAccountState } from '../../../../redux/reducers/accountReducer';
import { Column, Row } from '../../../common';
import { AddCircle12Regular } from '@fluentui/react-icons';
import { AddEditTaskDialog } from '../../../dialogs/AddEditTaskDialog';
import {
    makeStyles,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    MenuPopover,
    MenuTrigger,
    Text,
    tokens,
} from '@fluentui/react-components';
import { TaskControl } from '../../../controls/TaskControl';
import { Task, TaskStatus } from '../../../../data/types/commonTypes';
import { UserAccount } from '../../../../data/types/authTypes';
import { useAccess } from '../../../../auth/useAccess';
import { useCaseServicesContext } from '../Cases/CaseServicesContext';
import { LinkButton } from '../../../controls/LinkButton';
import { useState } from 'react';
import { DropdownMenu } from '../../../controls/Menus/DropdownMenu';

export const useStyles = makeStyles({
    noEvents: {
        color: tokens.colorNeutralForeground3,
    },
    eventColumn: {
        maxHeight: '15rem',
        overflowY: 'auto',
    },
    taskControl: {
        width: '95%',
    },
    addContactBtn: {
        fontSize: '13px',
        paddingBlockStart: '0',
        paddingBlockEnd: '0',
        ':focus': {
            outline: 'none',
            boxShadow: 'none',
        },
    },
});

export type BillableTasksCardProps = {
    height?: string;
    caseId: string;
    billable: boolean;
};
export const TimeTrackingTasksCard = (props: BillableTasksCardProps) => {
    const { caseId, billable = false } = props;

    const { taskService, milestoneService } = useCaseServicesContext()!;

    //Hooks
    const { primaryAccount } = useAccountState();
    const { user } = (primaryAccount as UserAccount) ?? {};
    const access = useAccess(user);

    //State
    const [milestoneFilter, setMilestoneFilter] = useState<string>('');

    //Vars
    const openTasks = taskService
        .tasksByStatus(TaskStatus.open)
        .filter((x) => x.userId == user.userId && x.billable == billable);
    const billableText = billable ? 'Billable' : 'Non-Billable';

    //Logic
    const sortTasksByMilestone = (a: Task, b: Task) => {
        const aMs = milestoneService.findMilestone(a.milestoneId);
        const bMs = milestoneService.findMilestone(b.milestoneId);
        return (aMs?.sortKey || 0) - (bMs?.sortKey || 0);
    };
    const filterByMilestone = (task: Task) => {
        return !milestoneFilter || milestoneFilter === 'all' ? true : task.milestoneId == milestoneFilter;
    };

    //UI Events
    const onCompleteTask = (task: Task) => () => taskService.onComplete(task);
    const onCloseTask = (task: Task) => () => taskService.onClose(task);

    return (
        <Column gap='medium'>
            <Row>
                <Column>
                    <Text size={400} weight='semibold'>{`${billableText} Tasks`}</Text>
                </Column>
                <Column width='auto' vAlign='middle'>
                    {access.cases.canCreateTasks() && (
                        <AddEditTaskDialog
                            onSubmit={taskService.upsertTaskAsync}
                            initialCaseId={caseId}
                            billableOnly={billable}
                        >
                            <LinkButton
                                icon={<AddCircle12Regular />}
                                text={`Add ${billableText} Task`}
                                data-testid='time-tracking-tasks-add-task-button'
                            />
                        </AddEditTaskDialog>
                    )}
                </Column>
            </Row>
            <Row>
                <Column width='auto' vAlign='middle'>
                    <DropdownMenu 
                        title='Milestone'
                        size='small'
                        options={[
                            { value: 'all', label: 'All Milestones' },
                            ...milestoneService.milestones.sort((a, b) => (a.sortKey || 0) - (b.sortKey || 0)).map((ms, idx) => ({
                                value: ms.milestoneId!,
                                label: `#${idx + 1} - ${ms.name}`,
                            })),
                        ]}
                        initialValue='all'
                        dataTestId='time-tracking-tasks-milestone-filter-menu'
                        onOptionChanged={(option) => setMilestoneFilter(option)}
                        width='fit-content'
                    />
                </Column>
            </Row>
            <Column gap='medium' scroll>
                {!!openTasks.length && (
                    <>
                        {openTasks
                            .filter(filterByMilestone)
                            .sort(sortTasksByMilestone)
                            .map((task: Task) => (
                                <Column key={task.taskId} height='auto'>
                                    <TaskControl
                                        task={task}
                                        acctUsers={primaryAccount?.accountUsers}
                                        onComplete={onCompleteTask(task)}
                                        onClose={onCloseTask(task)}
                                        onEdit={taskService.upsertTaskAsync}
                                        showMilestone
                                        showCase={false}
                                    />
                                </Column>
                            ))}
                    </>
                )}

                {!openTasks.length && (
                    <Column gap='medium' align='center' padding='pt-2'>
                        <Text size={400}>There are no {billableText.toLowerCase()} tasks remaining.</Text>
                    </Column>
                )}
            </Column>
        </Column>
    );
};
